import React, { useEffect, useRef, useState, useMemo } from 'react';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { asyncContainer, Typeahead } from 'react-bootstrap-typeahead';
import { checkIsNumber, shortenandHoverString } from '../../../../../Components/helper';
import CustomTooltip from '../../../../../Components/Tooltip/CustomTooltip';
import { isValidGradingInput } from '../../../../../utils/calculationHelpers';
import Loader from '../../../../../Components/Utility/Loader/Loader';
import GradingGroupTabs from './GradingGroupTabs';

const AsyncTypeahead = asyncContainer(Typeahead);

function SpecificationsView({
  sizeRange,
  onChangeGradGroup,
  state,
  onSortColumns,
  headerSortingIcon,
  handleAllMeasurement,
  setDiscrepancy,
  handleComment,
  isSupplierSpecification,
  isSupplierLoggedIn,
  SampleAddEditAccess,
  selectedGradGroup,
  gradingGroups,
  measurementAddEditRights,
  isProductArchived,
  importSource,
  onImportSourceChange,
  importLoader,
  importSearchResults,
  onImportSearch,
  onImportSelect,
  typeaheadRef,
  onManualPomFieldChange,
  onDeletePom,
  onSavePom,
}) {
  const [collapsedSizes, setCollapsedSizes] = useState({});
  const selectedGradGroupRef = useRef(selectedGradGroup);
  selectedGradGroupRef.current = selectedGradGroup;

  useEffect(() => {
    if (gradingGroups?.length > 0 && isEmpty(selectedGradGroupRef.current)) {
      onChangeGradGroup(gradingGroups[0]);
    }
  }, [gradingGroups]);

  useEffect(() => {
    setCollapsedSizes({});
  }, [selectedGradGroup?.value]);

  const toggleSizeCollapse = (sizeName) => {
    setCollapsedSizes((prev) => ({
      ...prev,
      [sizeName]: !prev[sizeName],
    }));
  };

  const collapseAll = () => {
    const allCollapsed = {};
    if (sizeNames && sizeNames.length) {
      sizeNames.forEach((size) => {
        allCollapsed[size] = true;
      });
    }
    setCollapsedSizes(allCollapsed);
  };

  const expandAll = () => {
    setCollapsedSizes({});
  };

  const { t } = useTranslation('product-sample');

  const checkClassColor = (item) => {
    let colorClass = null;

    if (item?.discrepancyColor === 'green') {
      colorClass = 'text-green';
    } else if (item?.discrepancyColor === 'red') {
      colorClass = 'text-red';
    }
    return colorClass;
  };
  const checkIsZeroExist = state?.sampleSpecificationList?.find((val) => val.isZero === true);
  const sampleSizeName = checkIsZeroExist?.sizeName || null;
  const sizeNames = useMemo(() => {
    if (!sizeRange || sizeRange.length === 0) return [];
    return sizeRange.map((s) => s.size || s.label.split(',')[0].trim());
  }, [sizeRange]);

  const groupedData = useMemo(() => {
    const grouped = {};
    if (state?.sampleSpecificationList?.length > 0) {
      state.sampleSpecificationList.forEach((item, index) => {
        const key = item.tempId || item.code || item.description || index;
        if (!grouped[key]) {
          grouped[key] = {
            tempId: item.tempId || null,
            code: item.code,
            description: item.description,
            tolMinus: item.tolMinus,
            sizes: {},
            isZero: item.isZero,
            sourceType: item.sourceType,
            apiIndex: index,
          };
        }
        grouped[key].sizes[item.sizeName] = { ...item, originalIndex: index };
      });
    }

    return Object.values(grouped)
      .map((pom) => {
        const visibleSizeName = sizeNames.find((s) => pom.sizes[s]) || Object.keys(pom.sizes)[0];
        const refItem = pom.sizes[visibleSizeName];
        const seq = typeof refItem?.sequence === 'number' ? refItem.sequence : null;
        return {
          ...pom,
          sequence: seq,
          comments: refItem?.comments,
          supplierComments: refItem?.supplierComments,
        };
      })
      .sort((firstPom, secondPom) => {
        if (firstPom.sequence != null && secondPom.sequence != null && firstPom.sequence !== secondPom.sequence) {
          return firstPom.sequence - secondPom.sequence;
        }
        return firstPom.apiIndex - secondPom.apiIndex;
      });
  }, [state?.sampleSpecificationList, sizeNames]);

  const getTotalColSpan = () => {
    let colCount = 3;
    sizeNames.forEach((sizeName) => {
      const isCollapsed = collapsedSizes[sizeName] !== undefined ? collapsedSizes[sizeName] === true : false;
      if (isCollapsed) {
        colCount += 1;
      } else {
        let expandedCols = 3;
        if (!isSupplierSpecification) expandedCols += 1;
        if (sizeName === sampleSizeName) expandedCols += 1;
        colCount += expandedCols;
      }
    });
    colCount += 2;
    return colCount;
  };

  const renderContent = () => {
    if (state.loading) return <Loader />;
    if (!gradingGroups?.length) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-5 text-muted">
          <span>{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.NO_GRADING_SIZE_FOUND')}</span>
        </div>
      );
    }
    if (!selectedGradGroup?.value) {
      return (
        <div className="d-flex justify-content-center align-items-center mt-5 text-muted">
          <span>{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.SELECT_GRADING_GROUP')}</span>
        </div>
      );
    }
    return (
      <div className="table-responsive sample-spec-scroll-container">
        <table className="table common-custom-table fixed-header layout-fixed sample-specification-table font-13 mb-0">
          <thead>
            <tr>
              <th
                className="w-150px"
                onClick={() => {
                  onSortColumns('code', state?.columnSortValue);
                }}
              >
                <div className="d-flex align-items-center">
                  {sizeNames.length > 1 &&
                    (() => {
                      const allExpanded = sizeNames.length > 0 && sizeNames.every((sizeName) => collapsedSizes[sizeName] !== true);
                      return (
                        <button
                          type="button"
                          title={allExpanded ? t('Collapse All') : t('Expand All')}
                          onClick={(e) => {
                            e.stopPropagation();
                            allExpanded ? collapseAll() : expandAll();
                          }}
                          className="expand-all-size"
                        >
                          {allExpanded ? <i className="icon-white-chevron-down left-direction" /> : <i className="icon-white-chevron-down right-direction" />}
                        </button>
                      );
                    })()}
                  {headerSortingIcon(t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.POM_CODE'), 'code')}
                </div>
              </th>

              <th
                className="w-220px"
                onClick={() => {
                  onSortColumns('description', state?.columnSortValue);
                }}
              >
                {headerSortingIcon(t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.POM_DESCRIPTION'), 'description')}
              </th>

              <th className="w-100px first-fix-col">
                {headerSortingIcon(t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.TOL'), t('TOL'))}
              </th>

              {sizeNames.map((sizeName) => {
                const isCollapsed = collapsedSizes[sizeName] !== undefined ? collapsedSizes[sizeName] === true : false;

                const toggleIcon = (
                  <button
                    className="btn p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCollapsedSizes((prev) => ({ ...prev, [sizeName]: !isCollapsed }));
                    }}
                  >
                    {isCollapsed ? '+' : '-'}
                  </button>
                );

                if (isCollapsed) {
                  return (
                    <th key={sizeName} className="w-100px text-center">
                      {sizeNames.length > 1 && <div className="expand-size-container">{toggleIcon}</div>}
                      <div className="size-container">{sizeName}</div>
                    </th>
                  );
                }

                return (
                  <React.Fragment key={sizeName}>
                    <th className="w-100px text-center">
                      {sizeNames.length > 1 && <div className="expand-size-container">{toggleIcon}</div>}
                      <div className="size-container">{sizeName}</div>
                    </th>
                    <th className="w-120px">{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.SUPLIER_MEASUREMENT')}</th>
                    {!isSupplierSpecification && (
                      <th className="w-120px">{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.ACTUAL_MEASURMENT')}</th>
                    )}
                    <th className="w-100px">{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.MEASURMENT_DISCRPANCY')}</th>
                    {sizeName === sampleSizeName && (
                      <th className="w-120px">{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.REVISED_MEASURMENT')}</th>
                    )}
                  </React.Fragment>
                );
              })}

              <th className="w-150px last-fix-col">{t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.COMMENT')}</th>
              <th className="w-80px" />
            </tr>
          </thead>

          <tbody>
            {groupedData.length > 0 &&
              groupedData.map((pom) => {
                return (
                  <tr key={pom.tempId || pom.code}>
                    <td>
                      <div className={sizeNames.length > 1 ? 'padding-left-expand-width' : ''}>
                        {pom.tempId ? (
                          <input
                            className="form-control input-border"
                            value={pom.code}
                            disabled={!SampleAddEditAccess || isProductArchived}
                            onChange={(e) => onManualPomFieldChange(pom.tempId, 'code', e.target.value)}
                          />
                        ) : (
                          pom.code
                        )}
                      </div>
                    </td>
                    <td>
                      {pom.tempId ? (
                        <div className="position-relative">
                          <input
                            className="form-control input-border"
                            value={pom.description}
                            disabled={!SampleAddEditAccess || isProductArchived}
                            maxLength={200}
                            onChange={(e) => onManualPomFieldChange(pom.tempId, 'description', e.target.value.slice(0, 200))}
                          />
                          <div className="text-count position-absolute right-zero">
                            {(pom.description || '').length}
                            /200
                          </div>
                        </div>
                      ) : (
                        shortenandHoverString(pom.description, 40)
                      )}
                    </td>
                    <td className="text-left first-fix-col">
                      {pom.tempId ? (
                        <input
                          className="form-control input-border"
                          value={pom.tolMinus || ''}
                          disabled={!SampleAddEditAccess || isProductArchived}
                          onChange={(e) => onManualPomFieldChange(pom.tempId, 'tolMinus', e.target.value)}
                        />
                      ) : (
                        pom.tolMinus
                      )}
                    </td>

                    {sizeNames.map((sizeName) => {
                      const item = pom.sizes[sizeName];
                      const isCollapsed = collapsedSizes[sizeName] !== undefined ? collapsedSizes[sizeName] === true : false;

                      if (!item) {
                        if (isCollapsed) return <td key={sizeName}>-</td>;
                        let colSpan = isSupplierSpecification ? 3 : 4;
                        if (!isSupplierSpecification && sizeName === sampleSizeName) colSpan = 5;
                        return (
                          <td key={sizeName} colSpan={colSpan}>
                            -
                          </td>
                        );
                      }

                      const i = item.originalIndex;

                      if (isCollapsed) {
                        return (
                          <td key={sizeName} className="text-center">
                            {item?.requestedMeasurement}
                          </td>
                        );
                      }

                      return (
                        <React.Fragment key={sizeName}>
                          <td className="text-center">{item?.requestedMeasurement}</td>
                          <td>
                            <div className="common-input-control mb-0">
                              <input
                                className="form-control input-border"
                                disabled={!!pom.tempId || !SampleAddEditAccess || isProductArchived}
                                value={item?.supplierMeasurement || ''}
                                onKeyPress={(e) => {
                                  const allowed = /[0-9\+\-\.\sNA\/]/;
                                  if (!allowed.test(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onBlur={() => setDiscrepancy(i)}
                                onChange={(ele) => {
                                  const val = ele.target.value;
                                  if (isValidGradingInput(val)) {
                                    handleAllMeasurement(i, ele, 'supplierMeasurement');
                                  }
                                }}
                              />
                            </div>
                          </td>
                          {!isSupplierSpecification && (
                            <td>
                              <div className="common-input-control mb-0">
                                <input
                                  className="form-control input-border"
                                  onKeyPress={(e) => {
                                    const allowed = /[0-9\+\-\.\sNA\/]/;
                                    if (!allowed.test(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(ele) => {
                                    const val = ele.target.value;
                                    if (isValidGradingInput(val)) {
                                      handleAllMeasurement(i, ele, 'actualMeasurement');
                                    }
                                  }}
                                  disabled={!!pom.tempId || isSupplierLoggedIn || !SampleAddEditAccess || isProductArchived}
                                  onBlur={() => setDiscrepancy(i)}
                                  value={item?.actualMeasurement || ''}
                                />
                              </div>
                            </td>
                          )}
                          <td className="text-left">
                            <span className={checkClassColor(item)}>
                              <b>{item?.discrepancy}</b>
                            </span>
                          </td>
                          {sizeName === sampleSizeName && (
                            <td>
                              <div className="common-input-control mb-0">
                                <input
                                  onChange={(ele) => {
                                    if (checkIsNumber(ele.target.value)) {
                                      handleAllMeasurement(i, ele, 'revisedMeasurement');
                                    }
                                  }}
                                  disabled={
                                    (pom.tempId && sizeName !== sampleSizeName) ||
                                    isSupplierLoggedIn ||
                                    !SampleAddEditAccess ||
                                    !measurementAddEditRights ||
                                    isProductArchived
                                  }
                                  className="form-control input-border"
                                  value={item?.revisedMeasurement || ''}
                                />
                              </div>
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}

                    {isSupplierSpecification ? (
                      <td className="last-fix-col">
                        <div className="common-input-control mb-0">
                          <input
                            disabled={!SampleAddEditAccess || isProductArchived}
                            onChange={(ele) => {
                              const indices = sizeNames.map((sName) => pom.sizes[sName]?.originalIndex).filter((i) => i != null);
                              handleComment(indices, ele.target.value, 'supplierComments');
                            }}
                            className="form-control input-border"
                            value={pom.supplierComments || ''}
                          />
                        </div>
                      </td>
                    ) : (
                      <td className="last-fix-col">
                        <div className="common-input-control mb-0">
                          <input
                            onChange={(ele) => {
                              const indices = sizeNames.map((sName) => pom.sizes[sName]?.originalIndex).filter((i) => i != null);
                              handleComment(indices, ele.target.value, 'comments');
                            }}
                            disabled={isSupplierLoggedIn || !SampleAddEditAccess || isProductArchived}
                            className="form-control input-border"
                            value={pom.comments || ''}
                          />
                        </div>
                      </td>
                    )}
                    <td>
                      <div className="d-flex align-items-center">
                        {pom.tempId && SampleAddEditAccess && !isProductArchived && (
                          <button aria-label="Save" type="submit" className="cursor-pointer text-decoration-none btn p-0 mr-8 " onClick={() => onSavePom(pom)}>
                            <i className="icon-Save f-20" />
                          </button>
                        )}
                        {pom.sourceType === 'manual' && SampleAddEditAccess && !isProductArchived && !isSupplierLoggedIn && !isSupplierSpecification && (
                          <button
                            aria-label="Delete Manual POM"
                            type="submit"
                            className="cursor-pointer text-decoration-none btn p-0 mr-8 "
                            onClick={() => onDeletePom(pom.tempId || pom.code)}
                          >
                            <i className="icon-Delete f-24" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            {!state.loading && groupedData.length === 0 && (
              <tr>
                <td colSpan={getTotalColSpan()} className="text-center py-4 text-muted">
                  {t('DEVLOPMENT_ORDER.SAMPLES.SAMPLE_DETAILS.SPECIFICATION.SPECIFICATION_VIEW.NO_POM_LIBRARY_FOUND')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const filterByLibrary = () => true;

  const importOptions = [
    {
      id: 'POM Library',
      value: 'POM Library',
      label: (
        <CustomTooltip placement="right" tooltipText={t('MEASUREMENT.POM_LIBRARY')}>
          <i className="icon-Inside-Measurements f-24" />
        </CustomTooltip>
      ),
    },
    {
      id: 'Grading Group',
      value: 'Grading Group',
      label: (
        <CustomTooltip placement="right" tooltipText={t('MEASUREMENT.GRADING_GROUP')}>
          <i className="icon-Library-1 f-24" />
        </CustomTooltip>
      ),
    },
  ];
  const borderColor = '1px solid #848484';
  const customStyles = {
    option: (provided) => ({
      ...provided,
      padding: '5px 10px',
    }),
    control: () => ({
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      border: 'none',
      height: '32px',
      minWidth: '75px',
      padding: '5px 0 5px 5px',
      fontSize: '14px',
      background: 'transparent',
      borderRadius: 0,
    }),
    singleValue: (provided, stateSingle) => {
      const opacity = stateSingle.isDisabled ? 0.5 : 1;
      const transition = 'opacity 300ms';

      return { ...provided, opacity, transition };
    },
    menu: (provided) => ({
      ...provided,
      margin: '-1px 0 0 0',
      border: borderColor,
      borderRadius: 0,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: 0,
      color: '#555',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#848484',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none',
    }),
  };
  return (
    <>
      <div className="new-tab-conatiner">
        <div className="new-tab-list">
          <div className="tab-container pb-0">
            <GradingGroupTabs
              gradingGroups={gradingGroups}
              selectedGradGroup={selectedGradGroup}
              onChangeGradGroup={onChangeGradGroup}
              isProductArchived={isProductArchived}
            />
          </div>
        </div>
      </div>

      {SampleAddEditAccess && !isProductArchived && selectedGradGroup?.value && !isSupplierLoggedIn && !isSupplierSpecification && (
        <div className="filter-library-container mt-16">
          <div className="filter-container w-400px">
            <div className="spec-search-input-group mb-16">
              <div className="spec-search-icon-select">
                <Select
                  options={importOptions}
                  styles={customStyles}
                  className="common-select"
                  classNamePrefix="common-select"
                  value={importSource}
                  onChange={onImportSourceChange}
                  isSearchable={false}
                  menuPosition="fixed"
                />
              </div>
              <div className="spec-search-typeahead">
                <AsyncTypeahead
                  ref={typeaheadRef}
                  labelKey="code"
                  isLoading={importLoader}
                  onSearch={onImportSearch}
                  options={importSearchResults || []}
                  onChange={onImportSelect}
                  minLength={1}
                  filterBy={filterByLibrary}
                  useCache={false}
                  placeholder="Search Library/Grading POM..."
                  renderMenuItemChildren={(option) => (
                    <div className="list-text">
                      {importSource?.id === 'POM Library' ? 'POM - ' : 'Grading POM - '}
                      {option.code} - {option.description}
                    </div>
                  )}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {renderContent()}
    </>
  );
}

export default SpecificationsView;
